import { ImageIcon } from "@phosphor-icons/react/dist/csr/Image";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useQuery } from "@tanstack/react-query";
import type {
  ClipboardEvent,
  Dispatch,
  DragEvent,
  ReactNode,
  SetStateAction,
} from "react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { useApi } from "@/core/api";

// Screenshots shown to the assistant (the job costing agent chat and a
// report's Ask Volca chat). Not documents: a report never reads them. The API
// holds the same limits; no GIF, as OpenAI refuses animated ones.
export const MAX_CHAT_IMAGES = 5;
const MAX_CHAT_IMAGE_BYTES = 5 * 1024 * 1024;
const CHAT_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];

/** A screenshot sent with a message, as the API lists it. */
export interface ChatImage {
  id: string;
  file_name: string;
}

/**
 * Adds dropped or pasted screenshots to the message being written.
 * `refusedHint` says where a file that is not a screenshot goes instead.
 */
export const useChatImages = (
  [images, setImages]: [File[], Dispatch<SetStateAction<File[]>>],
  { refusedHint }: { refusedHint?: string } = {},
) => {
  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    const accepted = files.filter((file) =>
      CHAT_IMAGE_TYPES.includes(file.type),
    );
    if (accepted.length < files.length)
      toast.error("Only PNG, JPEG or WebP images can be added here", {
        description: refusedHint,
      });
    const tooLarge = accepted.find((file) => file.size > MAX_CHAT_IMAGE_BYTES);
    if (tooLarge) {
      toast.error(`${tooLarge.name} is larger than 5 MB`);
      return;
    }
    if (images.length + accepted.length > MAX_CHAT_IMAGES) {
      toast.error(`A message can show up to ${MAX_CHAT_IMAGES} images`);
      return;
    }
    // Sliced again: two pastes before a render both pass the check above.
    if (accepted.length > 0)
      setImages((current) =>
        [...current, ...accepted].slice(0, MAX_CHAT_IMAGES),
      );
  };
  const removeImage = (image: File) =>
    setImages((current) => current.filter((other) => other !== image));
  return { images, addFiles, removeImage };
};

const draggingFiles = (event: DragEvent) =>
  event.dataTransfer.types.includes("Files");

/** Takes screenshots dropped anywhere over its children, laid out as a column. */
export const ChatImageDropZone = ({
  onFiles,
  disabled = false,
  className,
  children,
}: {
  onFiles: (files: File[]) => void;
  /** Takes nothing: a drop is left to the browser, which ignores it over the page. */
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) => {
  // dragenter/dragleave fire for every child crossed; count them.
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  return (
    <Stack
      className={cn("relative", className)}
      data-testid="chat-image-drop-zone"
      onDragEnter={(event) => {
        if (disabled || !draggingFiles(event)) return;
        event.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(event) => {
        if (!draggingFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = disabled ? "none" : "copy";
      }}
      onDragLeave={(event) => {
        if (!draggingFiles(event)) return;
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setDragging(false);
      }}
      onDrop={(event) => {
        if (!draggingFiles(event)) return;
        event.preventDefault();
        depth.current = 0;
        setDragging(false);
        if (!disabled) onFiles(Array.from(event.dataTransfer.files));
      }}
    >
      {children}
      {dragging && (
        <Stack
          align="center"
          justify="center"
          gap={2}
          className="pointer-events-none absolute inset-0 z-10 rounded-lg border-2 border-dashed border-focus bg-surface-overlay/90"
        >
          <ImageIcon aria-hidden className="size-6 text-subtle" />
          <Text variant="label">Drop a screenshot to show Volca</Text>
        </Stack>
      )}
    </Stack>
  );
};

/**
 * Picks screenshots from disk, for anyone who cannot drag or paste. `iconOnly`
 * fits it inside a composer, beside the send button.
 */
export const ChatImagePicker = ({
  onFiles,
  disabled = false,
  iconOnly = false,
}: {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  iconOnly?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={CHAT_IMAGE_TYPES.join(",")}
        className="hidden"
        data-testid="chat-images-input"
        onChange={(event) => {
          onFiles(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
      {iconOnly ? (
        <Button
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label="Add screenshot"
          title="Add screenshot"
          onClick={() => inputRef.current?.click()}
        >
          <ImageIcon />
        </Button>
      ) : (
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <ImageIcon />
          Add screenshot
        </Button>
      )}
    </>
  );
};

/**
 * Takes a pasted screenshot rather than letting it reach the textarea.
 * Copied cells come with a picture of them too: the table is what was meant.
 * (A copied file or image may carry its name or URL as text: still an image.)
 */
export const pasteChatImages = (
  event: ClipboardEvent,
  addFiles: (files: File[]) => void,
) => {
  const images = Array.from(event.clipboardData.files).filter((file) =>
    file.type.startsWith("image/"),
  );
  if (
    images.length === 0 ||
    /<table/i.test(event.clipboardData.getData("text/html"))
  )
    return;
  event.preventDefault();
  addFiles(images);
};

const useObjectUrl = (blob: Blob | undefined) => {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!blob) return;
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
};

const Thumbnail = ({
  url,
  name,
  small = false,
}: {
  url: string | undefined;
  name: string;
  small?: boolean;
}) =>
  url ? (
    <a href={url} target="_blank" rel="noreferrer" title={name}>
      <img
        src={url}
        alt={name}
        className={cn(
          "max-w-full rounded-lg border border-line object-contain",
          { "max-h-16": small, "max-h-32": !small },
        )}
      />
    </a>
  ) : (
    <Stack
      align="center"
      justify="center"
      className="h-20 w-28 rounded-lg border border-line bg-surface-raised"
    >
      <ImageIcon aria-label={name} className="size-5 text-subtle" />
    </Stack>
  );

/**
 * A screenshot waiting to be sent, with a way to take it out; without
 * `onRemove`, one just sent that the transcript shows before it is saved.
 */
export const PendingChatImage = ({
  image,
  onRemove,
}: {
  image: File;
  onRemove?: () => void;
}) => {
  const url = useObjectUrl(image);
  if (!onRemove) return <Thumbnail url={url} name={image.name} />;
  return (
    <div className="relative" data-testid="pending-chat-image">
      <Thumbnail url={url} name={image.name} small />
      <Button
        variant="secondary"
        size="icon"
        aria-label={`Remove ${image.name}`}
        className="absolute -top-2 -right-2 size-6 rounded-full border border-line shadow-raised"
        onClick={onRemove}
      >
        <XIcon aria-hidden />
      </Button>
    </div>
  );
};

/**
 * A screenshot sent with a message, read with the page's auth header from
 * `path`, the chat's `…/images/:id`.
 */
export const SentChatImage = ({
  path,
  image,
}: {
  path: string;
  image: ChatImage;
}) => {
  const api = useApi();
  const query = useQuery({
    // Not under the chat's key: refreshing the conversation must not refetch every image.
    queryKey: ["chat-image", path],
    queryFn: ({ signal }) =>
      api
        .get<Blob>(path, { responseType: "blob", signal })
        .then((res) => res.data),
    staleTime: Infinity,
    retry: false,
  });
  const url = useObjectUrl(query.data);
  if (query.isError)
    return (
      <Stack
        align="center"
        justify="center"
        gap={1}
        className="h-20 w-28 rounded-lg border border-line bg-surface-raised p-2"
      >
        <Text variant="caption" tone="muted">
          Not loaded
        </Text>
        <Button
          variant="secondary"
          size="sm"
          aria-label={`Retry loading ${image.file_name}`}
          disabled={query.isFetching}
          aria-busy={query.isFetching}
          onClick={() => void query.refetch()}
        >
          {query.isFetching ? "Retrying…" : "Retry"}
        </Button>
      </Stack>
    );
  return <Thumbnail url={url} name={image.file_name} />;
};
